import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Shield, Lock, FileText, Scale, CheckCircle, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBranding } from "../hooks/useBranding";

export default function Legal() {
  const { t } = useTranslation();
  const { branding } = useBranding();

  const sections = [
    { 
        title: t('legal.terms_title'), 
        icon: <FileText className="text-indigo-400" />,
        content: branding.page_terms_content || t('legal.terms_content')
    },
    { 
        title: t('legal.privacy_title'), 
        icon: <Lock className="text-cyan-400" />,
        content: branding.page_privacy_content || t('legal.privacy_content')
    },
    { 
        title: t('legal.aml_kyc_title'), 
        icon: <Shield className="text-emerald-400" />,
        content: branding.page_aml_content || t('legal.aml_kyc_content')
    },
    { 
        title: t('legal.risk_title'), 
        icon: <Scale className="text-amber-400" />,
        content: branding.page_risk_content || t('legal.risk_content')
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white font-inter">
      <Header />
      
      {/* Header */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute top-0 right-0 w-full max-w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-[150px] pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 z-10 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 mb-8">
                <CheckCircle size={14} className="text-emerald-400" />
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none tracking-tighter">{t('legal.compliance_badge')}</span>
            </div>
          <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tighter leading-tight font-outfit uppercase">
            {t('legal.title')} <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">{t('legal.title_highlight')}</span>
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto font-medium leading-relaxed">
            {t('legal.subtitle')}
          </p>
        </div>
      </section>

      {/* Legal Content */}
      <section className="py-24 relative">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8">
            {sections.map((section, idx) => (
              <div key={idx} className="glass-card p-10 border-white/5 hover:border-white/10 transition-colors group">
                <div className="flex items-center gap-6 mb-8">
                  <div className="p-4 bg-white/5 rounded-2xl border border-white/5 group-hover:scale-110 transition-transform">
                    {section.icon}
                  </div>
                  <h2 className="text-3xl font-black font-outfit uppercase tracking-tight">{section.title}</h2>
                </div>
                <div className="prose prose-invert max-w-none">
                    <p className="text-slate-400 text-lg leading-relaxed font-medium">
                        {section.content}
                    </p>
                </div>
                <div className="mt-8 pt-8 border-t border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-black text-slate-600 uppercase tracking-widest">
                        <Info size={14} /> {t('legal.last_update')}
                    </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-20 p-12 glass-card border-indigo-500/10 text-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 to-cyan-500 opacity-30"></div>
                <h3 className="text-2xl font-black font-outfit uppercase mb-6">{t('legal.more_info_title')}</h3>
                <p className="text-slate-400 font-medium mb-8">{t('legal.more_info_desc')}</p>
                <a href="mailto:legal@ennovatrade.global" className="inline-flex items-center gap-3 text-indigo-400 font-black text-sm uppercase tracking-widest hover:text-indigo-300 transition-colors">
                    legal@ennovatrade.global
                    <FileText size={18} />
                </a>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
